import { useCallback, useRef, useState } from 'react'

/**
 * Reports whether an element's content is clipped (line-clamp or text-overflow), so a tooltip can be
 * shown only when the label is actually truncated. Re-measures on resize.
 */
export function useIsTruncated<T extends HTMLElement = HTMLElement>() {
  const [isTruncated, setIsTruncated] = useState(false)
  const observerRef = useRef<ResizeObserver | null>(null)

  // Re-attaches the observer on each (re)mount of the span, keeping it on the live node. The span
  // remounts whenever the tooltip wrapper toggles on and off as the label crosses the truncation point.
  const ref = useCallback((node: T | null) => {
    observerRef.current?.disconnect()
    if (!node) return
    const measure = () => setIsTruncated(node.scrollHeight > node.clientHeight || node.scrollWidth > node.clientWidth)
    measure()
    observerRef.current = new ResizeObserver(measure)
    observerRef.current.observe(node)
  }, [])

  return { ref, isTruncated }
}
